import React from 'react'
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom'
import { blue } from '@mui/material/colors'
import { Alert, Button } from '@mui/material'
import { TitledPaper } from '@dai/components'
import { User } from 'firebase/auth'
import HandleVerifyEmail from './HandleVerifyEmail'
import SendVerificationEmail from './SendVerificationEmail'

export interface VerifyEmailProps {
  oobCode: string | null
  continueUrl: string
  signInUrl: string
  navigateToUpdateEmail: () => void
  sendEmailVerification: () => Promise<void>
  verifyEmail: (oobCode: string) => Promise<void>
  user: User | null
  isAuthenticated: boolean
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({
  oobCode,
  continueUrl,
  signInUrl,
  navigateToUpdateEmail,
  sendEmailVerification,
  verifyEmail,
  user,
  isAuthenticated,
}) => {
  const location = useLocation()
  const navigate = useNavigate()

  if (user?.emailVerified) {
    return (
      <TitledPaper
        title={'Verify Email'}
        subtitle={<span style={{ color: blue[500] }}>{user?.email}</span>}
      >
        <Alert severity="success">Your email is verified</Alert>
        <Button
          fullWidth
          color="primary"
          variant="contained"
          component={RouterLink}
          to={continueUrl}
          size="large"
        >
          Continue
        </Button>
      </TitledPaper>
    )
  }

  if (oobCode) {
    return (
      <HandleVerifyEmail
        oobCode={oobCode}
        continueUrl={continueUrl}
        verifyEmail={verifyEmail}
      />
    )
  }

  if (isAuthenticated) {
    return (
      <TitledPaper
        title={'Verify Email'}
        subtitle={
          <>
            <span style={{ color: blue[500] }}>{user?.email}</span>
            <br />
            We need to verify this email is correct, please check your inbox and
            verify your email.
          </>
        }
      >
        <SendVerificationEmail
          navigateToUpdateEmail={navigateToUpdateEmail}
          sendEmailVerification={sendEmailVerification}
          user={user}
        />
      </TitledPaper>
    )
  }

  return (
    <TitledPaper
      title={'Verify Email'}
      subtitle={'Please sign in to continue verifying your email address'}
    >
      <Button
        variant="contained"
        fullWidth
        onClick={() =>
          navigate(signInUrl, {
            state: {
              from: location.pathname + location.search,
            },
            replace: true,
          })
        }
      >
        Sign In
      </Button>
    </TitledPaper>
  )
}

export default VerifyEmail
